// #920 — the single source of truth for the gallery's user-facing "Collections"
// vocabulary.
//
// WHY THIS FILE EXISTS
// The gallery's folder surface is being renamed "Folder" -> "Collection" across
// headings, empty states, kebab items, confirm dialogs and the create/edit
// dialog. Those strings were previously scattered across six components, which
// is exactly how a rename ends up half-done (one screen says "Folder", the next
// says "Collection"). Every user-facing noun now comes from here, so the label
// test can assert ONE contract instead of chasing string literals.
//
// SCOPE: NAME ONLY. The folder MODEL, its API paths (`/api/v2/folders/`), the
// redux slice, the visibility/permission flags and the kebab's action set are
// deliberately untouched — a collection IS a folder, renamed and re-skinned.
// Internal identifiers therefore still read "folder" on purpose.

export const COLLECTION_NOUN = "Collection";
export const COLLECTION_NOUN_PLURAL = "Collections";

// QA-1261 — hard cap on a collection Title. Matches the wheel-name input
// (CreateWheelPopup/Sections/WheelInfo maxLength={100}) so the two name fields
// agree; before this the Title field had no maxlength at all (unbounded).
export const COLLECTION_TITLE_MAX_LENGTH = 100;

// BUG-953: cap the Collection description shown in a share preview
// (og:description) so a share card can't be blown out by an unbounded blurb.
export const COLLECTION_DESCRIPTION_MAX_LENGTH = 280;

/**
 * The gallery section heading. `count` of null/undefined means "still loading,
 * count unknown" and renders the bare noun rather than a misleading "(0)".
 */
export const collectionsHeading = (count) =>
  count == null
    ? COLLECTION_NOUN_PLURAL
    : `${COLLECTION_NOUN_PLURAL} (${count})`;

// Kebab menu labels. gordon-0921-WEB-collection-kebab-remove-change-to-
// private-visibility-lives-under-edit: the quick visibility toggles
// (Change to Public/Private, Show to visitors) were removed from here —
// visibility now lives ONLY under `settings` ("Edit", WheelVisibilityOptions
// in CreateFolder.jsx).
export const COLLECTION_MENU_LABELS = {
  chooseCover: "Change Portrait",
  share: "Share",
  settings: "Edit",
  moveTo: `Move to ${COLLECTION_NOUN.toLowerCase()}`,
  hideFromVisitors: "Hide from visitors",
  // bug#953: copies a public share link that resolves directly to this
  // collection (GET /api/v2/folders/shared/<slug>/).
  copyShareLink: "Copy share link",
  delete: "Delete",
  // gordon-0916-guest-collection-kebab: the only other entry a VISITOR gets
  // (alongside `share`) on someone else's collection -- see guestCollectionMenu.js.
  report: "Report",
};

// bug#953 kebab "Copy share link" toasts + guard message.
export const COLLECTION_SHARE_LINK_COPY = {
  copiedToast: "Share link copied to clipboard!",
  errorToast: "Couldn't get a share link for this collection",
  // Gordon decision 2026-09-02 (reconfirmed 2026-09-21): Public AND Private
  // are both link-shareable now -- only a Hidden ("visible only to me")
  // collection has no share control at all.
  hiddenToast: `This ${COLLECTION_NOUN.toLowerCase()} is hidden and can't be shared`,
};

// Wheel-card kebab items that reference a collection (folderWheelMenu.js).
export const WHEEL_MENU_LABELS = {
  removeFromCollection: `Remove from ${COLLECTION_NOUN.toLowerCase()}`,
  moveToCollection: `Move to ${COLLECTION_NOUN.toLowerCase()}`,
};

// T22.196 (Kelly 2026-10-13): inside a collection the wheel kebab lists
// "Delete" right next to "Remove from collection". Delete removes the wheel
// from the whole Gallery (every collection), so its confirm must say so and
// point at the non-destructive item instead.
export const WHEEL_IN_COLLECTION_DELETE_COPY = {
  note: `This deletes the wheel from your whole Gallery, not just this ${COLLECTION_NOUN.toLowerCase()}. To keep the wheel and only take it out of this ${COLLECTION_NOUN.toLowerCase()}, use "${WHEEL_MENU_LABELS.removeFromCollection}" instead. This action cannot be undone.`,
  confirmLabel: "Delete from Gallery",
};

// Create / edit dialog (Folders/Components/CreateFolder.jsx).
export const COLLECTION_DIALOG_COPY = {
  createTitle: `Create ${COLLECTION_NOUN}`,
  editTitle: `Edit ${COLLECTION_NOUN}`,
  // Gordon 2026-08-27: the input carries no separate label above it, so the
  // placeholder alone names the field — "Title", not "Collection Title".
  titleFieldPlaceholder: "Title",
  titleFieldAriaLabel: `${COLLECTION_NOUN} title`,
  titleRequiredError: `${COLLECTION_NOUN} title is a required field`,
  // BUG-953: shown in the Share preview's og:description when set.
  descriptionFieldPlaceholder: "Description (optional)",
  descriptionFieldAriaLabel: `${COLLECTION_NOUN} description`,
  descriptionMaxLength: 500,
  createdToast: `${COLLECTION_NOUN} Created Successfully`,
  // edit-collection-hide-wheels-copy (Gordon, 2026-09-01): renamed from "Hide
  // Contents from Gallery" -- "Contents" read ambiguous next to a Collection
  // that can itself hold sub-collections; "Wheels" says exactly what this
  // toggle keeps out of the Wheel Grid.
  hideContentsLabel: "Hide Wheels from Gallery",
  // ISP-9999 (Gordon #920hide, 2026-08-29): supersedes the 2026-08-27 copy,
  // which read as "hides the Collection from the Gallery" and got confused
  // with the Visibility control below it. This toggle and Visibility are
  // separate: it only keeps the Collection's Wheels out of the Wheel Grid
  // (they still show inside the Collection); it never changes whether the
  // Collection itself is visible.
  hideContentsTooltip: `Turning this on keeps this ${COLLECTION_NOUN}'s Wheels out of the Wheel Grid below the ${COLLECTION_NOUN_PLURAL} carousel — they still show inside the ${COLLECTION_NOUN}. It doesn't change whether the ${COLLECTION_NOUN} itself is visible to others.`,
};

// Confirm dialogs + toasts owned by OwnerView.
export const COLLECTION_CONFIRM_COPY = {
  deleteTitle: `Delete ${COLLECTION_NOUN}`,
  // G-163: names the collection being deleted so a misclick in the tight
  // kebab row is obvious before it's confirmed — falls back to the old
  // generic copy only when the collection has no title.
  deleteSubtitle: (title) =>
    title
      ? `Are you sure you want to delete "${title}"?`
      : `Are you sure you want to delete this ${COLLECTION_NOUN}?`,
  deletedToast: `${COLLECTION_NOUN} Deleted Successfully`,
  deleteErrorToast: `Failed to delete ${COLLECTION_NOUN.toLowerCase()}`,
  moveTitle: `Move ${COLLECTION_NOUN}`,
  moveNote: "Its contents move with it. You can drag it back out any time.",
  moveConfirmLabel: "Move",
  loadErrorToast: `Failed to load ${COLLECTION_NOUN_PLURAL.toLowerCase()}`,
  coverErrorToast: `Failed to update ${COLLECTION_NOUN.toLowerCase()} cover`,
};

// Empty states (EmptyStates.jsx).
export const COLLECTION_EMPTY_STATE_COPY = {
  emptyOpenedTitle: `This ${COLLECTION_NOUN.toLowerCase()} is empty`,
  emptyOpenedBody:
    "Drag wheels here to add them, or use the “Move to…” menu on any wheel.",
  // fix-kelly-0929-collections-video-audit item 6: a folder that HAS
  // subfolders (rendered above this panel) but zero DIRECT wheels is not
  // "empty" — saying so directly under a visible subfolder tile reads as a
  // lie. Distinct copy for that case; the top-level "nothing at all" case
  // above keeps saying "empty".
  noWheelsInFolderTitle: "No wheels here yet",
  noWheelsInFolderBody:
    "Drag wheels here to add them, or use the “Move to…” menu on any wheel.",
  allEmptyOwner: `Your ${COLLECTION_NOUN_PLURAL.toLowerCase()} are empty — drag wheels into a ${COLLECTION_NOUN.toLowerCase()} to fill it.`,
  allEmptyGuest: "No wheels to show yet.",
  newCollectionButton: `New ${COLLECTION_NOUN.toLowerCase()}`,
  firstRunBody: `Create your first wheel, then drag it into a ${COLLECTION_NOUN.toLowerCase()} to start organizing.`,
  // QA-1030: the in-gallery search box (#954) filters BOTH the collections
  // carousel and the wheels grid. When a query matches NEITHER, the panel used
  // to render "Collections (0)" over a bare "Wheels" heading and an empty void
  // with zero feedback — inconsistent with an empty source tab (Cloned/Unlocked/
  // Favorited), which shows a proper CollectionEmpty. This copy powers a
  // matching search-no-results empty state (SearchNoResults in EmptyStates.jsx).
  searchNoResultsTitle: (query) =>
    query ? `No results for “${query}”` : "No results",
  searchNoResultsBody: `No wheels or ${COLLECTION_NOUN_PLURAL.toLowerCase()} match your search. Try a different term.`,
};

// gordon-0917-collections-empty-state-own-gallery-web-rn: the owner's own
// gallery with ZERO collections shows a "Collections" heading over a
// dashed-border card (plus circle on the left) instead of the bare add-tile
// the carousel otherwise shows — RN mirrors this copy byte for byte.
export const COLLECTIONS_EMPTY_STATE_CARD_COPY = {
  heading: COLLECTION_NOUN_PLURAL,
  cardTitle: `Create a ${COLLECTION_NOUN.toLowerCase()}`,
  cardBody:
    "Group Wheels that belong together. Visitors see your public ones.",
};

// Controls (toggle, add-new card, carousel).
export const COLLECTION_CONTROL_LABELS = {
  createCollection: `Create ${COLLECTION_NOUN.toLowerCase()}`,
  addCollection: `Add ${COLLECTION_NOUN}`,
  carouselRegion: `${COLLECTION_NOUN_PLURAL} carousel`,
  scrollPrevious: `Scroll ${COLLECTION_NOUN_PLURAL.toLowerCase()} left`,
  scrollNext: `Scroll ${COLLECTION_NOUN_PLURAL.toLowerCase()} right`,
  breadcrumb: `${COLLECTION_NOUN.toLowerCase()} breadcrumb`,
  privateBadge: "Private — hidden from visitors",
  filterByCollection: `Filter wheels by ${COLLECTION_NOUN.toLowerCase()}`,
  allCollections: "All",
  // ISP-9999 #954: this box filters wheels AND collections together (one
  // query, both lists) — the copy says so rather than reading as
  // collections-only.
  // Kelly 2026-08-24 #3 (kelly-0824-search-button-word-cutoff): dropped the
  // trailing "..." — it added ~9px (measured 190px full vs 181px words-only on
  // web-stg f1097468, DM Sans 14px) with no meaning, and Kelly's target string
  // is "Search wheels & collections" with nothing after it. The width fix
  // proper is the flex-shrink change on the wrapper Box in OwnerView.jsx.
  // stgfix-1007-P0-gallery-search-all-tabs-always-visible (Kelly video
  // 2026-10-07, item 3): the box now also stays visible and searches on the
  // Cloned/Unlocked/Favorited tabs, not just Created — the bare "Search
  // Wheels & Collections" copy read as Created-only (matching its old
  // Created-only behavior) and didn't signal the widened scope. Added "all"
  // (+4 chars, DM Sans 14px ~ +12-16px measured) -- still well inside the
  // 226px inputClientWidth the 2026-08-24 fix above established (45px of
  // headroom over the 181px old label; this label measures ~193-197px,
  // still >25px of margin at both 1440 and 390 per
  // OwnerView.collectionsSearchWidth.test.jsx and
  // collections-search-truncation.spec.ts, both updated for the new string).
  searchPlaceholder: `Search all Wheels & ${COLLECTION_NOUN_PLURAL}`,
  searchAriaLabel: `Search wheels and ${COLLECTION_NOUN_PLURAL.toLowerCase()}`,
  // gordon-1005-P1-team-view-search-r2: the Team context has no folders/
  // collections concept (personal-only), so its search box is wheels-only —
  // distinct copy from the Personal box above rather than reusing it.
  teamSearchPlaceholder: "Search wheels",
  teamSearchAriaLabel: "Search wheels in this team",
  // gordon-1005-P1-gallery-collaborations-view ADDENDUM 08:55 PT: "The
  // Collaborations view MUST have the same search bar as Personal" — same
  // SearchField component (reused in GalleryCollaborationsView.jsx) as Team's,
  // own copy since it searches shared wheels, not this viewer's own.
  collaborationsSearchPlaceholder: "Search shared wheels",
  collaborationsSearchAriaLabel: "Search wheels shared with you",
  // stgfix-1007-P1-gallery-collaborations-view-missing-tabs-people-avatars:
  // the "I shared" tab searches the viewer's OWN shared wheels, not wheels
  // shared WITH them — own copy so the aria-label stays accurate per tab.
  collaborationsSearchAriaLabelIShared: "Search wheels you've shared",
};

// stgfix-1007-P1-gallery-collaborations-view-missing-tabs-people-avatars:
// per-tab copy for GalleryCollaborationsView's loading/error/empty states —
// gordon-1005-P1 only ever shipped the "Shared with me" half of these
// strings (hardcoded, see COLLECTION_CONTROL_LABELS above); "I shared" needs
// its own since the direction of sharing is reversed.
export const COLLABORATIONS_TAB_COPY = {
  shared_with_me: {
    label: "Shared with me",
    loadingCaption: "Loading wheels shared with you…",
    errorText: "Couldn't load wheels shared with you.",
    emptyTitle: "No shared wheels yet",
    emptyBody: "Wheels other people share with you will appear here.",
  },
  i_shared: {
    label: "I shared",
    loadingCaption: "Loading wheels you've shared…",
    errorText: "Couldn't load wheels you've shared.",
    emptyTitle: "You haven't shared any wheels yet",
    emptyBody: "Invite a collaborator to one of your wheels and it will appear here.",
  },
  // review-1008-Frontend-6619: a People pick with no matching wheel on the
  // current tab — not the tab's "never shared" copy, which wrongly told a
  // viewer with shared wheels they had none.
  peopleNoResults: {
    title: "No wheels match the people you picked",
    body: "Untick a name under People to see more wheels.",
  },
};

// BUG-953 — the Share dialog (CollectionShareModal.jsx).
export const COLLECTION_SHARE_COPY = {
  title: `Share ${COLLECTION_NOUN.toLowerCase()}`,
  defaultDescription: `Check out this ${COLLECTION_NOUN.toLowerCase()} on iYuga.`,
  // slack-dev-0902-collections-share-rule-v2 / Gordon decision 2026-09-02:
  // Private is link-shareable -- anyone holding the link can view it, not
  // just the owner.
  privateNotice: "This collection is private — anyone with this link can view it.",
  copiedToast: "Link copied to clipboard!",
  // collection-share-modal-match-wheel: the Facebook/LinkedIn/X share-button
  // title, mirrors ShareModal/index.jsx's own `quote` ("Check out this
  // amazing wheel!") but names the collection so the shared post isn't generic.
  shareQuote: (title) =>
    title
      ? `Check out this ${COLLECTION_NOUN.toLowerCase()}: ${title}`
      : `Check out this ${COLLECTION_NOUN.toLowerCase()} on iYuga!`,
};

// #920d §4 — the "Choose cover" picker dialog.
export const COLLECTION_COVER_PICKER_COPY = {
  title: "Change Portrait",
  emptyBody: `Add a wheel to this ${COLLECTION_NOUN.toLowerCase()} to choose a cover for it.`,
  optionLabel: (wheelName) => `Use "${wheelName}" as cover`,
  // gordon-1003-P2-itovi-collection-cover-logo-and-custom-covers: "people
  // should be able to set cover images for Collections that aren't
  // necessarily wheels" -- the picker's second section, always shown
  // alongside the wheel options above (never only when the collection is
  // empty of wheels).
  customImageSectionLabel: "Or use your own image",
  cancel: "Cancel",
};

/**
 * aria-label for one collection card. Keeps the private suffix the folder tile
 * already carried so assistive tech still hears the visibility state.
 */
export const collectionCardLabel = (title, { isPrivate = false } = {}) =>
  `${COLLECTION_NOUN} ${title || ""}${isPrivate ? ", private" : ""}`;

// gordon-0921-move-to-collection-my-folders: MoveToFoldersModal's root
// breadcrumb crumb (the "Move to Collection" popup's own heading row) was
// hardcoded "My Folders" — Kelly's gallery/22 screenshot, owner-reported
// 2026-09-21. Named separately from COLLECTION_CONTROL_LABELS.breadcrumb,
// which is a different string (FoldersV2's own breadcrumb region aria-label).
export const MY_COLLECTIONS_BREADCRUMB = `My ${COLLECTION_NOUN_PLURAL}`;

// gordon-0921-move-to-collection-my-folders-plus-visible-folder-word-sweep:
// toasts fired by the FoldersV2 drag-and-drop move flow (useFolderMove.js).
export const COLLECTION_DRAG_MOVE_TOAST_COPY = {
  wheelRemoved: `Wheel removed from ${COLLECTION_NOUN.toLowerCase()}`,
  moved: `${COLLECTION_NOUN} moved`,
  moveFailed: `Failed to move ${COLLECTION_NOUN.toLowerCase()} — reverted`,
};

// T22.178: a wheel you co-edit but do not own sits in your Created grid, yet
// only its owner can file it in a collection. Say so instead of attempting
// the move and showing a bare failure.
export const notOwnerWheelMoveCopy = (ownerName) =>
  `This wheel belongs to ${ownerName || "another person"}. You can edit it, but only its owner can put it in a ${COLLECTION_NOUN.toLowerCase()}.`;

export const wheelMoveFailedCopy = (reason) =>
  reason
    ? `Couldn't move the wheel: ${reason}`
    : "Failed to move wheel — reverted";
